<script setup lang="ts">
/* Portage de app/layouts/default.vue : header collant + <main> scrollable.
 *
 * La hauteur réelle du header est exposée en variable CSS `--app-header-height`
 * (ResizeObserver) : le plateau Kanban s'y dimensionne à `100dvh - header`.
 *
 * Tant que le store d'authentification n'est pas prêt, on affiche le spinner
 * « Authentification… ».
 *
 * ⚠️ L'erreur technique de /api/me n'est PAS traitée ici : `status === 'error'`
 * est intercepté un cran plus haut, dans `App.vue`, pour couvrir aussi les
 * routes en layout `minimal` où ce composant n'est jamais monté.
 */
import { onBeforeUnmount, ref, watch } from 'vue';
import { SpinnerSize } from '@/shared/enums';
import { useAuthStore } from '@/auth/authStore';
import AppHeader from '@/components/app/AppHeader.vue';
import LoadingSpinner from '@/components/app/LoadingSpinner.vue';

const auth = useAuthStore();

const headerWrapper = ref<HTMLElement | null>(null);
const headerHeight = ref(57);
let headerObserver: ResizeObserver | null = null;

watch(headerWrapper, (element) => {
  headerObserver?.disconnect();
  if (!element) return;
  headerObserver = new ResizeObserver(() => {
    headerHeight.value = element.offsetHeight || 57;
  });
  headerObserver.observe(element);
});

onBeforeUnmount(() => headerObserver?.disconnect());
</script>

<template>
  <div
    class="flex flex-col h-screen overflow-hidden"
    :style="{ '--app-header-height': `${headerHeight}px` }"
  >
    <!-- Résolution de la session : spinner plein écran, comme le Nuxt -->
    <template v-if="auth.status !== 'ready'">
      <LoadingSpinner
        class="flex-1 flex items-center justify-center bg-pbg"
        :size="SpinnerSize.Lg"
        message="Authentification…"
      />
    </template>

    <template v-else>
      <div ref="headerWrapper">
        <AppHeader class="sticky top-0 z-10" />
      </div>
      <main class="flex-1 overflow-y-auto bg-pbg">
        <slot />
      </main>
    </template>
  </div>
</template>
